/**
 * 요양시설(common) 임금 자동 계산
 * 입력·결과 필드명 = DB 컬럼명(cwhd_*)
 */
import {
  calcSupportDeduction,
  calcUsualWage,
  calcWageCenterSavingsNontaxBase,
  isProductionWorkerPosition,
  formatWageNumber,
  parseHours,
  parseWageNumber,
  validateAgeInsurance,
  PRODUCTION_WORKER_USUAL_LIMIT,
  type DeductionRates,
} from './useWageCalculator';
import {
  splitCommonWagePremiumByProductionNontax,
  applyCommonWageAccountingLunchFill,
  calcCommonWageTaxableFromParts,
  getCommonWageAccountingLunch,
  getCommonWageLedgerGrossPay,
  type CommonWagePayPartsRow,
} from '@/policies/commonWageSettingPolicy';
import {
  shouldTruncateWageOnes,
  truncateWageOnes,
  truncateWagePremiumPair,
} from '@/policies/wageOnesTruncatePolicy';

export interface IncomeTaxRow {
  income1: number;
  income2: number;
  depe_1: number;
}

/** 모달 입력 → 계산용 숫자 (키 = cwhd_*) */
export interface CommonWageFormNumbers {
  cwhd_pay_calc_hourly: number;
  cwhd_work_days: number;
  cwhd_work_hours: number;
  /** 기본급→시간 모드에서 수동 입력된 기본급 (있으면 시급×시간 대신 사용) */
  cwhd_basic?: number;
  cwhd_overtime_hours: number;
  cwhd_night_hours: number;
  cwhd_holiday_hours: number;
  cwhd_dayoff: number;
  cwhd_senior: number;
  cwhd_sunim: number;
  cwhd_bonus: number;
  cwhd_bosu: number;
  cwhd_bojon: number;
  cwhd_other: number;
  cwhd_dang: number;
  cwhd_lunch: number;
  cwhd_car: number;
  cwhd_child: number;
}

/** cwhd_manual_flags JSON 내부 키 (DB 컬럼과 별도) */
export interface CommonWageManualFlags {
  yeon?: boolean;
  gun?: boolean;
  jang?: boolean;
  go?: boolean;
  /** 고용보험 근로자 (별도 관리) */
  l_go?: boolean;
  /** 고용보험 기관 (별도 관리) */
  c_go?: boolean;
  l_so?: boolean;
  l_ji?: boolean;
  l_jiwon?: boolean;
  l_sik?: boolean;
  l_etc?: boolean;
  l_yearend?: boolean;
  c_sil?: boolean;
  c_san?: boolean;
  c_out?: boolean;
  /** 근무·수당 금액 수동 입력 (자동계산 덮어쓰기 방지) */
  w_basic?: boolean;
  w_overtime?: boolean;
  w_night?: boolean;
  w_holiday?: boolean;
  w_ot_nontax?: boolean;
  w_n_ntax?: boolean;
  w_h_ntax?: boolean;
}

export interface CommonWageDeductForm {
  cwhd_l_yeon: number;
  cwhd_l_gun: number;
  cwhd_l_jang: number;
  cwhd_l_go: number;
  cwhd_l_so: number;
  cwhd_l_ji: number;
  cwhd_l_jiwon: number;
  cwhd_l_sik: number;
  cwhd_l_etc: number;
  cwhd_l_yearend: number;
  cwhd_c_yeon: number;
  cwhd_c_gun: number;
  cwhd_c_jang: number;
  cwhd_c_go: number;
  cwhd_c_sil: number;
  cwhd_c_san: number;
  cwhd_c_out: number;
}

export interface CommonWageSettings {
  cwhd_production_nontax: boolean;
  cwhd_lunch_expense: boolean;
  cwhd_is_owner: boolean;
  manualFlags?: CommonWageManualFlags;
  deductForm?: CommonWageDeductForm;
}

export interface CommonWageTaxRates {
  rateYeon: number;
  rateGun: number;
  rateJang: number;
  rateGo: number;
  rateSil: number;
  rateSan: number;
}

export interface CommonWageCalcInput {
  form: CommonWageFormNumbers;
  settings: CommonWageSettings;
  taxRates: CommonWageTaxRates;
  incomeTaxTable: IncomeTaxRow[];
  birthDate?: string | null;
  year: number;
  month: number;
  /** 중식 실비변상 채움 한도(중도 입·퇴) · 기존 저장분 보호용 */
  rowContext?: Pick<
    CommonWagePayPartsRow,
    | 'ce_join_date'
    | 'ce_leave_date'
    | 'clci_work_start_date'
    | 'clci_work_end_date'
    | 'is_month_enter'
    | 'is_month_leave'
    | 'cwhd_idx'
    | 'cwhd_taxable'
  >;
}

/** 계산 결과 (저장 컬럼명과 동일 + UI용 파생 필드) */
export interface CommonWageCalcResult {
  cwhd_basic: number;
  overtimeTotal: number;
  nightTotal: number;
  holidayTotal: number;
  cwhd_overtime: number;
  cwhd_night: number;
  cwhd_holiday: number;
  cwhd_overtime_nontax: number;
  cwhd_night_nontax: number;
  cwhd_holiday_nontax: number;
  cwhd_wage_total: number;
  cwhd_taxable: number;
  cwhd_usual: number;
  /** 실비변상 채움 후 중식(저장·표시와 별도, 회계 대장용). 임금설정 화면 비과세 합계에는 쓰지 않음 */
  cwhd_lunch?: number;
  cwhd_l_yeon: number;
  cwhd_l_gun: number;
  cwhd_l_jang: number;
  cwhd_l_go: number;
  cwhd_l_so: number;
  cwhd_l_ji: number;
  cwhd_l_jiwon: number;
  cwhd_l_sik: number;
  cwhd_l_etc: number;
  cwhd_l_yearend: number;
  cwhd_l_total: number;
  cwhd_c_yeon: number;
  cwhd_c_gun: number;
  cwhd_c_jang: number;
  cwhd_c_go: number;
  cwhd_c_sil: number;
  cwhd_c_san: number;
  cwhd_c_out: number;
  cwhd_c_total: number;
  netPay: number;
}

const truncateToTen = (value: number) => Math.floor(value / 10) * 10;

/**
 * 퇴직금 = 총 지급금액 ÷ 12.
 * 중식 실비변상일 때만 중식비를 뺀다.
 * 이때 중식비는 센터장 화면 금액이 아니라 한도까지 채운 회계 중식비다.
 */
function calcCommonWageRetirementAmount(wageTotal: number, lunch: number, lunchExpense: boolean): number {
  let base = wageTotal;
  if (lunchExpense) base -= lunch;
  return truncateToTen(Math.round(Math.max(0, base) / 12));
}

export function calculateIncomeTax(
  taxableIncome: number,
  incomeTaxTable: IncomeTaxRow[]
): number {
  if (!incomeTaxTable.length) return 0;
  for (const row of incomeTaxTable) {
    const minIncome = row.income1 * 1000;
    const maxIncome = row.income2 * 1000;
    if (taxableIncome >= minIncome && taxableIncome < maxIncome) {
      return Math.round(row.depe_1);
    }
  }
  return 0;
}

export type CommonWageDeductCalcResult = Pick<
  CommonWageCalcResult,
  | 'cwhd_l_yeon'
  | 'cwhd_l_gun'
  | 'cwhd_l_jang'
  | 'cwhd_l_go'
  | 'cwhd_l_so'
  | 'cwhd_l_ji'
  | 'cwhd_l_jiwon'
  | 'cwhd_l_sik'
  | 'cwhd_l_etc'
  | 'cwhd_l_yearend'
  | 'cwhd_l_total'
  | 'cwhd_c_yeon'
  | 'cwhd_c_gun'
  | 'cwhd_c_jang'
  | 'cwhd_c_go'
  | 'cwhd_c_sil'
  | 'cwhd_c_san'
  | 'cwhd_c_out'
  | 'cwhd_c_total'
  | 'netPay'
>;

/** 저장된 지급액·과세를 유지한 채 4대보험·공제만 재계산 (전월복사 후) */
export function computeCommonWageDeductionsFromTaxable(input: {
  wageTotal: number;
  taxable: number;
  lunch: number;
  /** 실비변상 시 퇴직금 차감용. 없으면 lunch(화면 입력)를 쓴다 */
  retirementLunch?: number;
  senior: number;
  sunim: number;
  workHours: number;
  settings: CommonWageSettings;
  taxRates: CommonWageTaxRates;
  incomeTaxTable: IncomeTaxRow[];
  birthDate?: string | null;
  year: number;
  month: number;
}): CommonWageDeductCalcResult {
  const {
    wageTotal,
    taxable,
    lunch,
    retirementLunch,
    senior,
    sunim,
    workHours,
    settings,
    taxRates,
    incomeTaxTable,
    birthDate,
    year,
    month,
  } = input;

  const ageExempt = validateAgeInsurance(birthDate, year, month);
  const applyPension = !ageExempt.pensionExempt;
  const applyEmployment = !ageExempt.employmentExempt;

  const auto_l_yeon = applyPension ? truncateToTen(Math.round(taxable * taxRates.rateYeon)) : 0;
  const auto_l_gun = truncateToTen(Math.round(taxable * taxRates.rateGun));
  const auto_l_jang = truncateToTen(Math.round(auto_l_gun * taxRates.rateJang));
  const auto_l_go =
    settings.cwhd_is_owner || !applyEmployment
      ? 0
      : truncateToTen(Math.round(taxable * taxRates.rateGo));
  const auto_l_so = truncateToTen(calculateIncomeTax(taxable, incomeTaxTable));
  const auto_l_ji = truncateToTen(Math.round(auto_l_so * 0.1));

  const supportRates: DeductionRates = {
    rateYeon: applyPension ? taxRates.rateYeon : 0,
    rateGun: taxRates.rateGun,
    rateJang: taxRates.rateJang,
    rateGo: settings.cwhd_is_owner || !applyEmployment ? 0 : taxRates.rateGo,
    rateSil: settings.cwhd_is_owner ? 0 : taxRates.rateSil,
    rateSan: settings.cwhd_is_owner ? 0 : taxRates.rateSan,
  };
  const auto_l_jiwon = calcSupportDeduction(
    {
      longterm: senior,
      sunim,
      workHours,
      yeonDeduct: applyPension,
      goDeduct: !settings.cwhd_is_owner && applyEmployment,
    },
    supportRates
  ).total;

  const flags = settings.manualFlags || {};
  const d = settings.deductForm;
  const flagOn = (key: keyof CommonWageManualFlags) => {
    const v = flags[key] as unknown;
    return v === true || v === 1 || v === '1' || v === 'Y' || v === 'y' || v === 'true';
  };

  // 수동 고정 체크 시 저장·입력값 유지 (나이 면제여도 체크면 유지 — 편집 UI와 동일)
  const cwhd_l_yeon = flagOn('yeon') && d ? truncateToTen(d.cwhd_l_yeon) : auto_l_yeon;
  const cwhd_l_gun = flagOn('gun') && d ? truncateToTen(d.cwhd_l_gun) : auto_l_gun;
  const cwhd_l_jang = flagOn('jang') && d ? truncateToTen(d.cwhd_l_jang) : auto_l_jang;
  const cwhd_l_go =
    flagOn('l_go') && d
      ? truncateToTen(d.cwhd_l_go)
      : settings.cwhd_is_owner || !applyEmployment
        ? 0
        : auto_l_go;
  const cwhd_l_so = flagOn('l_so') && d ? truncateToTen(d.cwhd_l_so) : auto_l_so;
  const cwhd_l_ji = flagOn('l_ji') && d ? truncateToTen(d.cwhd_l_ji) : auto_l_ji;
  const cwhd_l_jiwon = flagOn('l_jiwon') && d ? truncateToTen(d.cwhd_l_jiwon) : auto_l_jiwon;
  const auto_l_sik = truncateToTen(lunch);
  const cwhd_l_sik = flagOn('l_sik') && d ? truncateToTen(d.cwhd_l_sik) : auto_l_sik;
  const cwhd_l_etc = truncateToTen(d?.cwhd_l_etc ?? 0);
  const cwhd_l_yearend = truncateToTen(d?.cwhd_l_yearend ?? 0);

  const cwhd_l_total =
    cwhd_l_yeon +
    cwhd_l_gun +
    cwhd_l_jang +
    cwhd_l_go +
    cwhd_l_so +
    cwhd_l_ji +
    cwhd_l_jiwon +
    cwhd_l_sik +
    cwhd_l_etc +
    cwhd_l_yearend;

  const auto_c_yeon = applyPension ? truncateToTen(Math.round(taxable * taxRates.rateYeon)) : 0;
  const auto_c_gun = truncateToTen(Math.round(taxable * taxRates.rateGun));
  const auto_c_jang = truncateToTen(Math.round(auto_c_gun * taxRates.rateJang));
  const auto_c_go =
    settings.cwhd_is_owner || !applyEmployment
      ? 0
      : truncateToTen(Math.round(taxable * taxRates.rateGo));
  const auto_c_sil = settings.cwhd_is_owner
    ? 0
    : truncateToTen(Math.round(taxable * taxRates.rateSil));
  const auto_c_san = settings.cwhd_is_owner ? 0 : truncateToTen(Math.round(taxable * taxRates.rateSan));

  const lunchForRetirement = retirementLunch != null ? retirementLunch : lunch;
  const auto_c_out = settings.cwhd_is_owner
    ? 0
    : calcCommonWageRetirementAmount(wageTotal, lunchForRetirement, settings.cwhd_lunch_expense);

  const cwhd_c_yeon = flagOn('yeon') && d ? truncateToTen(d.cwhd_c_yeon) : auto_c_yeon;
  const cwhd_c_gun = flagOn('gun') && d ? truncateToTen(d.cwhd_c_gun) : auto_c_gun;
  const cwhd_c_jang = flagOn('jang') && d ? truncateToTen(d.cwhd_c_jang) : auto_c_jang;
  const cwhd_c_go =
    flagOn('c_go') && d
      ? truncateToTen(d.cwhd_c_go)
      : settings.cwhd_is_owner || !applyEmployment
        ? 0
        : auto_c_go;
  const cwhd_c_sil =
    flagOn('c_sil') && d
      ? truncateToTen(d.cwhd_c_sil)
      : settings.cwhd_is_owner
        ? 0
        : auto_c_sil;
  const cwhd_c_san =
    flagOn('c_san') && d ? truncateToTen(d.cwhd_c_san) : settings.cwhd_is_owner ? 0 : auto_c_san;
  const cwhd_c_out =
    flagOn('c_out') && d ? truncateToTen(d.cwhd_c_out) : settings.cwhd_is_owner ? 0 : auto_c_out;

  const cwhd_c_total = cwhd_c_yeon + cwhd_c_gun + cwhd_c_jang + cwhd_c_go + cwhd_c_sil + cwhd_c_san + cwhd_c_out;

  return {
    cwhd_l_yeon,
    cwhd_l_gun,
    cwhd_l_jang,
    cwhd_l_go,
    cwhd_l_so,
    cwhd_l_ji,
    cwhd_l_jiwon,
    cwhd_l_sik,
    cwhd_l_etc,
    cwhd_l_yearend,
    cwhd_l_total,
    cwhd_c_yeon,
    cwhd_c_gun,
    cwhd_c_jang,
    cwhd_c_go,
    cwhd_c_sil,
    cwhd_c_san,
    cwhd_c_out,
    cwhd_c_total,
    netPay: Math.max(0, wageTotal - cwhd_l_total),
  };
}

export function computeCommonWageDetail(input: CommonWageCalcInput): CommonWageCalcResult {
  const { form, settings, taxRates, incomeTaxTable, birthDate, year, month } = input;
  const f = form;

  // 시급×시간 — 2026-09부터 1의자리 절삭 (8월 이하 신고분은 반올림 유지)
  // 기본급→시간 모드에서는 form.cwhd_basic이 수동 입력값으로 들어옴
  const cwhd_basic = f.cwhd_basic != null && f.cwhd_basic > 0
    ? f.cwhd_basic
    : truncateWageOnes(f.cwhd_pay_calc_hourly * f.cwhd_work_hours, year, month);
  const overtimeTotal = truncateWageOnes(
    f.cwhd_pay_calc_hourly * f.cwhd_overtime_hours * 1.5,
    year,
    month
  );
  const nightTotal = truncateWageOnes(f.cwhd_pay_calc_hourly * f.cwhd_night_hours * 0.5, year, month);
  const holidayTotal = truncateWageOnes(
    f.cwhd_pay_calc_hourly * f.cwhd_holiday_hours * 1.5,
    year,
    month
  );

  const cwhd_usual = calcUsualWage({
    base: cwhd_basic,
    bonus: f.cwhd_bonus,
    edu: f.cwhd_bosu,
    bojon: f.cwhd_bojon,
    etc: f.cwhd_other,
    dang: f.cwhd_dang,
    lunch: f.cwhd_lunch,
    car: f.cwhd_car,
    childcare: f.cwhd_child,
  });

  const applyNontax =
    settings.cwhd_production_nontax && cwhd_usual <= PRODUCTION_WORKER_USUAL_LIMIT;
  const split = splitCommonWagePremiumByProductionNontax(
    overtimeTotal,
    nightTotal,
    holidayTotal,
    applyNontax
  );
  const cwhd_overtime_nontax = split.cwhd_overtime_nontax;
  const cwhd_night_nontax = split.cwhd_night_nontax;
  const cwhd_holiday_nontax = split.cwhd_holiday_nontax;
  const cwhd_overtime = split.cwhd_overtime;
  const cwhd_night = split.cwhd_night;
  const cwhd_holiday = split.cwhd_holiday;

  const cwhd_wage_total =
    cwhd_basic +
    overtimeTotal +
    nightTotal +
    holidayTotal +
    f.cwhd_dayoff +
    f.cwhd_senior +
    f.cwhd_sunim +
    f.cwhd_bonus +
    f.cwhd_bosu +
    f.cwhd_bojon +
    f.cwhd_other +
    f.cwhd_dang +
    f.cwhd_lunch +
    f.cwhd_car +
    f.cwhd_child;

  const totalNontax =
    cwhd_overtime_nontax +
    cwhd_night_nontax +
    cwhd_holiday_nontax +
    f.cwhd_dang +
    f.cwhd_lunch +
    f.cwhd_car +
    f.cwhd_child;
  const cwhd_taxable = Math.max(0, cwhd_wage_total - totalNontax);

  const deduct = computeCommonWageDeductionsFromTaxable({
    wageTotal: cwhd_wage_total,
    taxable: cwhd_taxable,
    lunch: f.cwhd_lunch,
    senior: f.cwhd_senior,
    sunim: f.cwhd_sunim,
    workHours: f.cwhd_work_hours,
    settings,
    taxRates,
    incomeTaxTable,
    birthDate,
    year,
    month,
  });

  return {
    cwhd_basic,
    overtimeTotal,
    nightTotal,
    holidayTotal,
    cwhd_overtime,
    cwhd_night,
    cwhd_holiday,
    cwhd_overtime_nontax,
    cwhd_night_nontax,
    cwhd_holiday_nontax,
    cwhd_wage_total,
    cwhd_taxable,
    cwhd_usual,
    ...deduct,
  };
}

/**
 * 실비변상 ON — 내부적으로만 중식 한도 채움 후 과세·공제 재산출
 * ★ 화면 지급칸(기본급·수당·중식·합계 등)은 입력/산출 원본 유지
 * ★ 과세·4대보험·공제·실수령은 회계(채움) 기준
 * ★ 퇴직금만 총 지급금액에서 회계 중식(한도 채움)을 빼고 ÷12
 * ★ 중식비공제(l_sik)는 화면 입력 중식비 유지
 */
export function finalizeCommonWageCalcWithLunchExpense(
  calc: CommonWageCalcResult,
  input: CommonWageCalcInput
): CommonWageCalcResult {
  const { form, settings, taxRates, incomeTaxTable, birthDate, year, month, rowContext } = input;
  if (!settings.cwhd_lunch_expense) return calc;

  const ym = `${year}${String(month).padStart(2, '0')}`;
  const parts: CommonWagePayPartsRow = {
    ...rowContext,
    cwhd_ym: ym,
    cwhd_idx: rowContext?.cwhd_idx,
    cwhd_taxable: rowContext?.cwhd_taxable,
    cwhd_basic: calc.cwhd_basic,
    cwhd_overtime: calc.cwhd_overtime,
    cwhd_overtime_nontax: calc.cwhd_overtime_nontax,
    cwhd_night: calc.cwhd_night,
    cwhd_night_nontax: calc.cwhd_night_nontax,
    cwhd_holiday: calc.cwhd_holiday,
    cwhd_holiday_nontax: calc.cwhd_holiday_nontax,
    cwhd_dayoff: form.cwhd_dayoff,
    cwhd_senior: form.cwhd_senior,
    cwhd_sunim: form.cwhd_sunim,
    cwhd_bonus: form.cwhd_bonus,
    cwhd_bosu: form.cwhd_bosu,
    cwhd_bojon: form.cwhd_bojon,
    cwhd_other: form.cwhd_other,
    cwhd_dang: form.cwhd_dang,
    cwhd_lunch: form.cwhd_lunch,
    cwhd_car: form.cwhd_car,
    cwhd_child: form.cwhd_child,
    cwhd_lunch_expense: true,
  };
  const filled = applyCommonWageAccountingLunchFill(parts, { year, month });
  const wageTotal = getCommonWageLedgerGrossPay(filled);
  const taxable = calcCommonWageTaxableFromParts(filled);
  const accountingLunch = Number(filled.cwhd_lunch) || 0;
  const deduct = computeCommonWageDeductionsFromTaxable({
    wageTotal,
    taxable,
    lunch: form.cwhd_lunch,
    retirementLunch: accountingLunch,
    senior: form.cwhd_senior,
    sunim: form.cwhd_sunim,
    workHours: form.cwhd_work_hours,
    settings,
    taxRates,
    incomeTaxTable,
    birthDate,
    year,
    month,
  });

  // 지급 항목은 calc 원본 유지 (기본급·중식 등 화면 변동 없음)
  return {
    ...calc,
    cwhd_taxable: taxable,
    ...deduct,
  };
}

/** 화면 표시용 — 지급·과세·비과세는 입력 원본, 4대보험·공제만 실비변상(회계) 기준 */
export function computeCommonWageDetailForDisplay(input: CommonWageCalcInput): CommonWageCalcResult {
  const raw = computeCommonWageDetail(input);
  const fin = finalizeCommonWageCalcWithLunchExpense(raw, input);
  if (!input.settings.cwhd_lunch_expense) return fin;
  return {
    ...fin,
    // 채움 중식비를 화면에 남기지 않음 (비과세액 합계 = 입력 중식 기준)
    cwhd_lunch: input.form.cwhd_lunch,
    cwhd_taxable: raw.cwhd_taxable,
    cwhd_usual: raw.cwhd_usual,
    cwhd_basic: raw.cwhd_basic,
    cwhd_wage_total: raw.cwhd_wage_total,
  };
}

/**
 * 저장용 — 지급칸은 채움 전(원본), 과세·공제만 실비변상 회계 기준
 */
export function computeCommonWageDetailForSave(input: CommonWageCalcInput): CommonWageCalcResult {
  const raw = computeCommonWageDetail(input);
  if (!input.settings.cwhd_lunch_expense) return raw;
  const display = finalizeCommonWageCalcWithLunchExpense(raw, input);
  return {
    ...raw,
    cwhd_taxable: display.cwhd_taxable,
    cwhd_l_yeon: display.cwhd_l_yeon,
    cwhd_l_gun: display.cwhd_l_gun,
    cwhd_l_jang: display.cwhd_l_jang,
    cwhd_l_go: display.cwhd_l_go,
    cwhd_l_so: display.cwhd_l_so,
    cwhd_l_ji: display.cwhd_l_ji,
    cwhd_l_jiwon: display.cwhd_l_jiwon,
    cwhd_l_sik: display.cwhd_l_sik,
    cwhd_l_etc: display.cwhd_l_etc,
    cwhd_l_yearend: display.cwhd_l_yearend,
    cwhd_l_total: display.cwhd_l_total,
    cwhd_c_yeon: display.cwhd_c_yeon,
    cwhd_c_gun: display.cwhd_c_gun,
    cwhd_c_jang: display.cwhd_c_jang,
    cwhd_c_go: display.cwhd_c_go,
    cwhd_c_sil: display.cwhd_c_sil,
    cwhd_c_san: display.cwhd_c_san,
    cwhd_c_out: display.cwhd_c_out,
    cwhd_c_total: display.cwhd_c_total,
    netPay: display.netPay,
  };
}

export interface CommonWageDeductionFixPreview {
  hasAuto: boolean;
  /** 나이·대표자 등 조건을 반영한 자동계산 (0원일 수 있음) */
  applied: number;
  /** 그 조건을 빼고 요율만 적용한 금액 */
  nominal: number;
  formula: string;
  notes?: string;
}

const DEDUCTION_FIX_RESULT_KEY: Partial<Record<keyof CommonWageManualFlags, keyof CommonWageCalcResult>> = {
  yeon: 'cwhd_l_yeon',
  gun: 'cwhd_l_gun',
  jang: 'cwhd_l_jang',
  l_go: 'cwhd_l_go',
  c_go: 'cwhd_c_go',
  l_so: 'cwhd_l_so',
  l_ji: 'cwhd_l_ji',
  l_jiwon: 'cwhd_l_jiwon',
  l_sik: 'cwhd_l_sik',
  c_sil: 'cwhd_c_sil',
  c_san: 'cwhd_c_san',
  c_out: 'cwhd_c_out',
};

function formatDeductionRatePercent(rate: number): string {
  const text = (rate * 100).toFixed(4).replace(/\.?0+$/, '');
  return `${text}%`;
}

/**
 * 공제 체크 모달 — 조건 반영 자동계산과, 면제 없이 요율만 적용한 금액을 함께 돌려준다.
 */
export function previewCommonWageDeductionFix(
  input: CommonWageCalcInput,
  flag: keyof CommonWageManualFlags
): CommonWageDeductionFixPreview {
  const empty: CommonWageDeductionFixPreview = { hasAuto: false, applied: 0, nominal: 0, formula: '' };
  const resultKey = DEDUCTION_FIX_RESULT_KEY[flag];
  if (!resultKey) return empty;

  const cleared: CommonWageCalcInput = {
    ...input,
    settings: {
      ...input.settings,
      manualFlags: { ...input.settings.manualFlags, [flag]: false },
    },
  };
  const result = computeCommonWageDetailForDisplay(cleared);
  const applied = Number(result[resultKey]) || 0;
  const taxable = Number(result.cwhd_taxable) || 0;
  const rates = input.taxRates;
  const f = formatWageNumber;
  const taxableFmt = f(taxable);
  const age = validateAgeInsurance(input.birthDate, input.year, input.month);
  const owner = !!input.settings.cwhd_is_owner;
  const ageText = age.age !== null ? ` (만 ${age.age}세)` : '';

  const nomYeon = truncateToTen(Math.round(taxable * rates.rateYeon));
  const nomGun = truncateToTen(Math.round(taxable * rates.rateGun));
  const nomJang = truncateToTen(Math.round(nomGun * rates.rateJang));
  const nomGo = truncateToTen(Math.round(taxable * rates.rateGo));
  const nomSo = truncateToTen(calculateIncomeTax(taxable, input.incomeTaxTable));
  const nomJi = truncateToTen(Math.round(nomSo * 0.1));
  const nomSil = truncateToTen(Math.round(taxable * rates.rateSil));
  const nomSan = truncateToTen(Math.round(taxable * rates.rateSan));
  const lunch = Number(input.form.cwhd_lunch) || 0;
  const wageTotal = Number(result.cwhd_wage_total) || 0;
  const accountingLunch = input.settings.cwhd_lunch_expense
    ? getCommonWageAccountingLunch(
        {
          ...input.rowContext,
          cwhd_ym: `${input.year}${String(input.month).padStart(2, '0')}`,
          cwhd_basic: Number(result.cwhd_basic) || 0,
          cwhd_lunch: lunch,
          cwhd_lunch_expense: true,
        },
        { year: input.year, month: input.month }
      )
    : lunch;
  const nomOut = calcCommonWageRetirementAmount(
    wageTotal,
    accountingLunch,
    input.settings.cwhd_lunch_expense
  );
  const wageTotalFmt = f(wageTotal);

  const senior = Number(input.form.cwhd_senior) || 0;
  const sunim = Number(input.form.cwhd_sunim) || 0;
  const workHours = Number(input.form.cwhd_work_hours) || 0;
  const nominalSupport = calcSupportDeduction(
    { longterm: senior, sunim, workHours, yeonDeduct: true, goDeduct: true },
    rates
  ).total;

  const rateLine = (rate: number, amount: number, extra?: string) =>
    [`과세임금 ${taxableFmt}원 × ${formatDeductionRatePercent(rate)}${extra ? ` ${extra}` : ''}`, `= ${f(amount)}원`].join('\n');

  let nominal = applied;
  let formula = '';
  const reasons: string[] = [];

  switch (flag) {
    case 'yeon':
      nominal = nomYeon;
      formula = rateLine(rates.rateYeon, nomYeon);
      if (age.pensionExempt) reasons.push(`만 60세 이상 국민연금 면제${ageText}`);
      break;
    case 'gun':
      nominal = nomGun;
      formula = rateLine(rates.rateGun, nomGun);
      break;
    case 'jang':
      nominal = nomJang;
      formula = [`건강보험료 ${f(nomGun)}원 × ${formatDeductionRatePercent(rates.rateJang)}`, `= ${f(nomJang)}원`].join('\n');
      break;
    case 'l_go':
      nominal = nomGo;
      formula = rateLine(rates.rateGo, nomGo, '(근로)');
      if (owner) reasons.push('대표자 고용보험 면제');
      else if (age.employmentExempt) reasons.push(`만 65세 이상 고용보험 면제${ageText}`);
      break;
    case 'c_go':
      nominal = nomGo;
      formula = rateLine(rates.rateGo, nomGo, '(기관)');
      if (owner) reasons.push('대표자 고용보험 면제');
      else if (age.employmentExempt) reasons.push(`만 65세 이상 고용보험 면제${ageText}`);
      break;
    case 'l_so':
      nominal = nomSo;
      formula = [`과세임금 ${taxableFmt}원 → 간이세액표`, `= ${f(nomSo)}원`].join('\n');
      break;
    case 'l_ji':
      nominal = nomJi;
      formula = [`소득세 ${f(nomSo)}원 × 10%`, `= ${f(nomJi)}원`].join('\n');
      break;
    case 'l_jiwon':
      nominal = nominalSupport;
      formula = [
        `장기근속 ${f(senior)}원 + 선임 ${f(sunim)}원`,
        `면제 없이 요율 적용 = ${f(nominalSupport)}원`,
      ].join('\n');
      if (age.pensionExempt) reasons.push(`만 60세 이상 국민연금 면제${ageText}`);
      if (owner) reasons.push('대표자 고용·실업·산재 면제');
      else if (age.employmentExempt) reasons.push(`만 65세 이상 고용보험 면제${ageText}`);
      break;
    case 'l_sik':
      nominal = truncateToTen(lunch);
      formula = `중식비 ${f(nominal)}원`;
      break;
    case 'c_sil':
      nominal = nomSil;
      formula = rateLine(rates.rateSil, nomSil, '(기관)');
      if (owner) reasons.push('대표자 실업급여 면제');
      break;
    case 'c_san':
      nominal = nomSan;
      formula = rateLine(rates.rateSan, nomSan);
      if (owner) reasons.push('대표자 산재보험 면제');
      break;
    case 'c_out':
      nominal = nomOut;
      formula = input.settings.cwhd_lunch_expense
        ? [`(총 지급금액 ${wageTotalFmt}원 − 회계 중식비 ${f(accountingLunch)}원) ÷ 12`, `= ${f(nomOut)}원`].join('\n')
        : [`총 지급금액 ${wageTotalFmt}원 ÷ 12`, `= ${f(nomOut)}원`].join('\n');
      if (owner) reasons.push('대표자 퇴직금 미적용');
      break;
    default:
      return empty;
  }

  const notes = applied !== nominal && reasons.length ? reasons.join('\n') : undefined;
  return { hasAuto: true, applied, nominal, formula, notes };
}

/** 저장·일괄재계산 — 이미 산출된 기본급·연야휴만 2026-09부터 원단위 절삭 */
export function applyWageOnesTruncateToCalcResult(
  calc: CommonWageCalcResult,
  year: number,
  month: number
): CommonWageCalcResult {
  if (!shouldTruncateWageOnes(year, month)) return calc;
  const basic = truncateWageOnes(calc.cwhd_basic, year, month);
  const ot = truncateWagePremiumPair(calc.cwhd_overtime, calc.cwhd_overtime_nontax, year, month);
  const night = truncateWagePremiumPair(calc.cwhd_night, calc.cwhd_night_nontax, year, month);
  const hol = truncateWagePremiumPair(calc.cwhd_holiday, calc.cwhd_holiday_nontax, year, month);
  const oldOt = calc.cwhd_overtime + calc.cwhd_overtime_nontax;
  const oldNight = calc.cwhd_night + calc.cwhd_night_nontax;
  const oldHol = calc.cwhd_holiday + calc.cwhd_holiday_nontax;
  const newOt = ot.tax + ot.nontax;
  const newNight = night.tax + night.nontax;
  const newHol = hol.tax + hol.nontax;
  const payDelta =
    calc.cwhd_basic - basic + (oldOt - newOt) + (oldNight - newNight) + (oldHol - newHol);
  const taxableDelta =
    calc.cwhd_basic -
    basic +
    (calc.cwhd_overtime - ot.tax) +
    (calc.cwhd_night - night.tax) +
    (calc.cwhd_holiday - hol.tax);
  return {
    ...calc,
    cwhd_basic: basic,
    overtimeTotal: newOt,
    nightTotal: newNight,
    holidayTotal: newHol,
    cwhd_overtime: ot.tax,
    cwhd_overtime_nontax: ot.nontax,
    cwhd_night: night.tax,
    cwhd_night_nontax: night.nontax,
    cwhd_holiday: hol.tax,
    cwhd_holiday_nontax: hol.nontax,
    cwhd_wage_total: calc.cwhd_wage_total - payDelta,
    cwhd_taxable: Math.max(0, calc.cwhd_taxable - taxableDelta),
    cwhd_usual: Math.max(0, calc.cwhd_usual - (calc.cwhd_basic - basic)),
  };
}

/** 시급×시간으로 산출되는 금액 — 수동 override 불가 (단, cwhd_basic은 기본급→시간 모드에서 수동 입력 허용) */
export const WAGE_HOURS_DERIVED_CALC_KEYS = new Set<string>([
  'cwhd_basic',
  'cwhd_overtime',
  'cwhd_night',
  'cwhd_holiday',
  'cwhd_overtime_nontax',
  'cwhd_night_nontax',
  'cwhd_holiday_nontax',
]);

/** cwhd_manual_flags — 시급×시간 자동산출 항목 (저장·로드 시 무시, 단 w_basic은 기본급→시간 모드에서 사용) */
export const WAGE_HOURS_DERIVED_MANUAL_FLAGS: (keyof CommonWageManualFlags)[] = [
  'w_basic',
  'w_overtime',
  'w_night',
  'w_holiday',
  'w_ot_nontax',
  'w_n_ntax',
  'w_h_ntax',
];

export function stripHoursDerivedManualFlags(flags: CommonWageManualFlags): CommonWageManualFlags {
  const next = { ...flags };
  for (const key of WAGE_HOURS_DERIVED_MANUAL_FLAGS) {
    delete next[key];
  }
  return next;
}

/** 자동계산 금액 필드 ↔ manual_flags 키 */
export const WAGE_AMOUNT_MANUAL_FLAG_BY_CALC_KEY: Record<string, keyof CommonWageManualFlags> = {
  cwhd_basic: 'w_basic',
  cwhd_overtime: 'w_overtime',
  cwhd_night: 'w_night',
  cwhd_holiday: 'w_holiday',
  cwhd_overtime_nontax: 'w_ot_nontax',
  cwhd_night_nontax: 'w_n_ntax',
  cwhd_holiday_nontax: 'w_h_ntax',
};

/** 수동 입력된 근무·수당 금액 반영 + 임금합계·과세·통상임금 재계산 */
export function applyWageAmountOverrides(
  calc: CommonWageCalcResult,
  overrides: Partial<Record<string, number>>,
  flags: CommonWageManualFlags,
  form: CommonWageFormNumbers,
  productionNontax: boolean
): CommonWageCalcResult {
  const c = { ...calc };
  for (const [key, flag] of Object.entries(WAGE_AMOUNT_MANUAL_FLAG_BY_CALC_KEY)) {
    if (WAGE_HOURS_DERIVED_CALC_KEYS.has(key) && key !== 'cwhd_basic') continue;
    if (flags[flag] && overrides[key] != null) {
      (c as Record<string, number>)[key] = Number(overrides[key]) || 0;
    }
  }

  const otFull = c.cwhd_overtime + c.cwhd_overtime_nontax;
  const nightFull = c.cwhd_night + c.cwhd_night_nontax;
  const holFull = c.cwhd_holiday + c.cwhd_holiday_nontax;
  const usual = calcUsualWage({
    base: c.cwhd_basic,
    bonus: form.cwhd_bonus,
    edu: form.cwhd_bosu,
    bojon: form.cwhd_bojon,
    etc: form.cwhd_other,
    dang: form.cwhd_dang,
    lunch: form.cwhd_lunch,
    car: form.cwhd_car,
    childcare: form.cwhd_child,
  });
  const split = splitCommonWagePremiumByProductionNontax(
    otFull,
    nightFull,
    holFull,
    productionNontax && usual <= PRODUCTION_WORKER_USUAL_LIMIT
  );
  c.cwhd_overtime = split.cwhd_overtime;
  c.cwhd_overtime_nontax = split.cwhd_overtime_nontax;
  c.cwhd_night = split.cwhd_night;
  c.cwhd_night_nontax = split.cwhd_night_nontax;
  c.cwhd_holiday = split.cwhd_holiday;
  c.cwhd_holiday_nontax = split.cwhd_holiday_nontax;
  c.cwhd_usual = usual;

  c.cwhd_wage_total =
    c.cwhd_basic +
    otFull +
    nightFull +
    holFull +
    form.cwhd_dayoff +
    form.cwhd_senior +
    form.cwhd_sunim +
    form.cwhd_bonus +
    form.cwhd_bosu +
    form.cwhd_bojon +
    form.cwhd_other +
    form.cwhd_dang +
    form.cwhd_lunch +
    form.cwhd_car +
    form.cwhd_child;

  const premiumNontax =
    c.cwhd_overtime_nontax + c.cwhd_night_nontax + c.cwhd_holiday_nontax;
  const totalNontax =
    premiumNontax + form.cwhd_dang + form.cwhd_lunch + form.cwhd_car + form.cwhd_child;
  c.cwhd_taxable = Math.max(0, c.cwhd_wage_total - totalNontax);
  c.netPay = c.cwhd_wage_total - c.cwhd_l_total;
  return c;
}

export function parseCommonWageForm(form: Record<string, string>): CommonWageFormNumbers {
  return {
    cwhd_pay_calc_hourly: parseWageNumber(form.cwhd_pay_calc_hourly),
    cwhd_work_days: parseInt(String(form.cwhd_work_days).replace(/[^\d]/g, ''), 10) || 0,
    cwhd_work_hours: parseHours(form.cwhd_work_hours),
    cwhd_overtime_hours: parseHours(form.cwhd_overtime_hours),
    cwhd_night_hours: parseHours(form.cwhd_night_hours),
    cwhd_holiday_hours: parseHours(form.cwhd_holiday_hours),
    cwhd_dayoff: parseWageNumber(form.cwhd_dayoff),
    cwhd_senior: parseWageNumber(form.cwhd_senior),
    cwhd_sunim: parseWageNumber(form.cwhd_sunim),
    cwhd_bonus: parseWageNumber(form.cwhd_bonus),
    cwhd_bosu: parseWageNumber(form.cwhd_bosu),
    cwhd_bojon: parseWageNumber(form.cwhd_bojon),
    cwhd_other: parseWageNumber(form.cwhd_other),
    cwhd_dang: parseWageNumber(form.cwhd_dang),
    cwhd_lunch: parseWageNumber(form.cwhd_lunch),
    cwhd_car: parseWageNumber(form.cwhd_car),
    cwhd_child: parseWageNumber(form.cwhd_child),
  };
}

export function taxRateInfoToRates(tax: {
  rateYeon: number | string;
  rateGun: number | string;
  rateJang: number | string;
  rateGo: number | string;
  rateSil: number | string;
  rateSan: number | string;
}): CommonWageTaxRates {
  return {
    rateYeon: Number(tax.rateYeon) || 0.045,
    rateGun: Number(tax.rateGun) || 0.03545,
    rateJang: Number(tax.rateJang) || 0.1295,
    rateGo: Number(tax.rateGo) || 0.009,
    rateSil: Number(tax.rateSil) || 0.009,
    rateSan: Number(tax.rateSan) || 0.007,
  };
}

export function defaultProductionNontax(position?: string | null): boolean {
  return isProductionWorkerPosition(position);
}

/** 인건비절감(c_save) 기준 비과세 — 비과세합계 − 차량 − 자녀 (요양시설·공통 임금) */
export function calcCommonWageCenterSavingsNontaxBase(
  totalNontax: number,
  car: number,
  child: number
): number {
  return calcWageCenterSavingsNontaxBase(totalNontax, car, child);
}
